import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Miembro } from '../lib/supabase';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { Textarea } from './ui/Textarea';
import { DialogShell } from './ui/DialogShell';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface SoftDeleteMiembroModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (motivo: string) => Promise<void>;
  miembro: Miembro | null;
}

export const SoftDeleteMiembroModal: React.FC<SoftDeleteMiembroModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  miembro
}) => {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const { error, clearError, handleError } = useErrorHandler();

  const resetAndClose = () => {
    if (!loading) {
      setMotivo('');
      handleError('');
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleError('');
    if (motivo.trim().length < 10) {
      handleError('El motivo debe tener al menos 10 caracteres');
      return;
    }
    try {
      setLoading(true);
      await onConfirm(motivo.trim());
      setMotivo('');
      onClose();
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos marcar al miembro sin derechos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell
      isOpen={isOpen && !!miembro}
      onClose={resetAndClose}
      onBeforeClose={() => false}
      size="sm"
      header={
        <div className="bg-amber-600 text-white p-6 flex items-center justify-between rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6" />
            <h2 className="text-xl font-bold">Marcar Miembro Sin Derechos</h2>
          </div>
          <button
            onClick={resetAndClose}
            disabled={loading}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            Estás a punto de marcar a <span className="font-bold">{miembro?.nombres} {miembro?.apellidos}</span> como
            sin derechos. Esta acción NO elimina al miembro de forma permanente, pero lo excluirá de las listas
            activas y estadísticas.
          </p>
        </div>

        <Textarea
          label={`Motivo del cambio de estado (${motivo.length}/10 caracteres mínimos)`}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Describe el motivo por el cual este miembro pierde sus derechos (mínimo 10 caracteres)"
          rows={4}
          required
          disabled={loading}
        />

        <FormErrorAlert error={error} onClear={clearError} />

        <div className="flex gap-3">
          <button
            type="button"
            onClick={resetAndClose}
            disabled={loading}
            className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || motivo.trim().length < 10}
            className="flex-1 px-6 py-3 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Procesando...' : 'Confirmar'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
